/**
 * Timezone helpers for the company setting that drives quote dates and expiry.
 *
 * The list comes from the runtime rather than a hand-maintained table, so it
 * never drifts from the zones the browser and the Worker can actually resolve.
 */

/** Enough to be usable if `Intl.supportedValuesOf` is missing. */
const FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Australia/Sydney',
  'Australia/Perth',
  'Pacific/Auckland',
];

/** The zone the device is set to, or null when it cannot be determined. */
export function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Every selectable zone, with `current` guaranteed present.
 *
 * A company already set to a zone this runtime does not enumerate must still
 * see its own value selected — otherwise opening Settings and pressing Save
 * would quietly move them to whichever zone happened to sort first.
 */
export function timezoneOptions(current: string): Array<{ value: string; label: string }> {
  let zones: string[];
  try {
    zones = Intl.supportedValuesOf('timeZone');
  } catch {
    zones = FALLBACK_TIMEZONES;
  }

  const all = zones.includes(current) ? zones : [current, ...zones];
  return all.map((zone) => ({ value: zone, label: zone.replace(/_/g, ' ') }));
}
